import { Box, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { GuidanceSection } from '../GuidanceSection';
import { WizardNavigation } from '../WizardNavigation';

/** Path to the checkmark icon */
const CHECKMARK_ICON_PATH = '/assets/images/installation-guide/checkmark-circle.svg';

/**
 * ReadinessCheckStep - Slide 2.2: Installation Readiness Check
 * 
 * Displays a checklist of items the user should have before starting
 * the installation process. Includes chatbot help buttons for WiFi
 * and video login assistance.
 */
export const ReadinessCheckStep: React.FC = () => {
  const { goToStep, setMainStepId } = useInstallationGuide();

  useEffect(() => {
    setMainStepId('step_1_preInstall');
  }, [setMainStepId]);

  const handleContinue = () => {
    goToStep('box-contents');
  };

  const checklistItems = [
    __(
      "The password for your camera or network video device(s) you'll be connecting to the Fusus Core."
    ),
    __('A power outlet the Core can reach.'),
    __('An empty spot on your Internet router.'),
    __("Your phone connected to the same network you're connecting the Core to."),
  ];

  const guidanceButtons = [
    { label: __("I'm not sure if I'm on WiFi"), flowId: 'wifi-help' },
    { label: __('Help find my video login'), flowId: 'credentials-help' },
  ];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
        {__('Installation readiness check.')}
      </Typography>

      <Typography variant="h6" sx={{ mb: 3, textAlign: 'left' }}>
        {__("Before we begin setting up your Core, let's make sure you have:")}
      </Typography>

      {/* Checklist */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          py: 1,
          mb: 2,
        }}
      >
        <List disablePadding>
          {checklistItems.map((item, index) => (
            <ListItem
              key={index}
              sx={{
                alignItems: 'flex-start',
                py: 1.5,
                px: 2,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  mt: 0.5,
                }}
              >
                <Box
                  component="img"
                  src={CHECKMARK_ICON_PATH}
                  alt=""
                  sx={{
                    width: 24,
                    height: 24,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={item}
                primaryTypographyProps={{
                  variant: 'body1',
                  sx: { color: 'text.primary' },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Guidance section with chatbot buttons */}
      <GuidanceSection
        buttons={guidanceButtons}
      />

      <WizardNavigation
        primaryLabel={__('Continue')}
        onPrimary={handleContinue}
      />
    </Box>
  );
};
